import { segmentKey, type SegmentId } from "@p2p-hls/protocol";

/**
 * Bounded cache of downloaded segment bytes plus the "have" index we announce
 * to peers. For LIVE we only need a sliding window near the live edge — old
 * segments are useless to share and just waste memory — so this is an LRU capped
 * by BOTH a segment count AND a byte budget. The byte budget is the important one:
 * a count-only cap floats with bitrate (30 × multi-MB segments = 60–240 MB of live
 * heap that churns every ~30 segments → non-deterministic major-GC pauses / freezes
 * on high-bitrate streams). The byte budget keeps the held bytes bounded regardless.
 */
export class SegmentStore {
  private readonly map = new Map<string, { seg: SegmentId; data: Uint8Array }>();
  private totalBytes = 0;

  /** @param maxSegments count cap; @param maxBytes byte budget (default: unbounded). */
  constructor(private readonly maxSegments: number, private readonly maxBytes = Infinity) {}

  get(seg: SegmentId): Uint8Array | undefined {
    const key = segmentKey(seg);
    const entry = this.map.get(key);
    if (!entry) return undefined;
    // LRU touch: re-insert to mark most-recently-used.
    this.map.delete(key);
    this.map.set(key, entry);
    return entry.data;
  }

  has(seg: SegmentId): boolean {
    return this.map.has(segmentKey(seg));
  }

  /** Insert bytes; returns the segments evicted so we can announce the drop. */
  put(seg: SegmentId, data: Uint8Array): SegmentId[] {
    const key = segmentKey(seg);
    const existing = this.map.get(key);
    if (existing) { this.totalBytes -= existing.data.byteLength; this.map.delete(key); }
    this.map.set(key, { seg, data });
    this.totalBytes += data.byteLength;
    const dropped: SegmentId[] = [];
    // Evict oldest over EITHER limit — but never the entry we just added (size > 1),
    // so a single huge segment can't evict itself.
    while (this.map.size > 1 && (this.map.size > this.maxSegments || this.totalBytes > this.maxBytes)) {
      const oldestKey = this.map.keys().next().value as string;
      const oldest = this.map.get(oldestKey)!;
      this.map.delete(oldestKey);
      this.totalBytes -= oldest.data.byteLength;
      dropped.push(oldest.seg);
    }
    return dropped;
  }

  /** All currently-held segment ids (for a full HAVE announce to a new peer). */
  held(): SegmentId[] {
    return [...this.map.values()].map((e) => e.seg);
  }
}
